import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { addToCartService } from "redux/services/general_service";

const ProductInfo = ({
  product = {},
  onAddToCart,
  onAddToWishlist,
  isInWishlist = false,
  isAddingToCart = false,
  isAddingToWishlist = false,
}) => {
  const { t, i18n } = useTranslation();

  const dispatch = useDispatch();

  const [selectedOptions, setSelectedOptions] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [groupedVariants, setGroupedVariants] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);

  // Mapping response data to component props
  const {
    name = "",
    price: originalPrice = 0,
    price_after_discount: discountedPrice = 0,
    variants = [],
    desc: description = "",
    attributes = [],
  } = product || {};

  // Group variants by their attribute type (color, size, etc.)
  useEffect(() => {
    const grouped = {};
    variants.forEach((variant) => {
      variant.values.forEach((value) => {
        const attrType = value.product_attribute_name;
        if (!grouped[attrType]) {
          grouped[attrType] = {
            name: attrType,
            options: [],
          };
        }
        // Avoid duplicate options
        if (
          !grouped[attrType].options.some((opt) => opt.value === value.value)
        ) {
          grouped[attrType].options.push({
            id: value.id,
            value: value.value,
            product_attribute_name: value.product_attribute_name,
          });
        }
      });
    });
    setGroupedVariants(grouped);
  }, [variants]);

  const hasDiscount =
    product?.price_after_discount !== null &&
    product?.price_after_discount !== 0
      ? true
      : false;
  const finalPrice = hasDiscount ? discountedPrice : originalPrice;
  const savings = originalPrice - discountedPrice;

  const handleOptionChange = (optionType, value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [optionType]: value,
    }));
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (onAddToCart) {
      if (attributes?.length == 0) {
        setSelectedVariant(variants[0]);
      }
      onAddToCart({
        product,
        selectedVariant,
        quantity,
      });
    }
  };

  const handleAddToWishlist = () => {
    if (onAddToWishlist) {
      onAddToWishlist(product);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(i18n.language === "en" ? "en" : "ar", {
      style: "currency",
      currency: "IQD",
    }).format(price);
  };

  // Render grouped variant options
  const renderVariantGroups = () => {
    return variants.map((variant) => {
      const variantText = variant.values.map((val) => val.value).join(" - ");
      return (
        <button
          key={variant.id}
          onClick={() => {
            setSelectedVariant(variant);
          }}
          className={`p-3 mb-2 border rounded-[4px] w-full text-right transition-smooth ${
            selectedVariant?.id === variant.id
              ? "border-primary bg-global-1 text-white"
              : "border-border text-text-primary hover:border-primary"
          }`}
        >
          <div className="flex justify-between items-center">
            <span className="font-semibold">{variantText}</span>
          </div>
          {/* Show quantity available only if it's less than or equal to 10 */}
          {variant.quantity <= 10 && (
            <div className="text-xs mt-1 text-right text-red-500">
              {t("quantityAvailable")}: {variant.quantity}
            </div>
          )}
        </button>
      );
    });
  };

  return (
    <div className="space-y-6 p-4 bg-white shadow-md rounded-[12px]">
      {/* Product Name */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-text-primary mb-2">
          {name}
        </h1>
      </div>

      {/* Pricing */}
      <div className="space-y-2">
        <div className="flex items-center space-x-3">
          <span className="text-3xl font-bold text-text-primary">
            {formatPrice(finalPrice)}
          </span>
          {hasDiscount && (
            <span className="text-xl text-text-secondary line-through">
              {formatPrice(originalPrice)}
            </span>
          )}
        </div>
        {hasDiscount && (
          <p className="text-sm text-success">
            {t("youSaved")} {formatPrice(savings)}
          </p>
        )}
      </div>

      {/* Product Options */}
      {Object.keys(groupedVariants).length > 0 && renderVariantGroups()}

      {/* Quantity Selector (only if available quantity is 10 or more) */}
      {selectedVariant && selectedVariant.quantity >= 0 && (
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            {t("quantity")}
          </label>
          <div className="flex items-center space-x-3">
            <div className="flex items-center border border-border rounded-md">
              <button
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={quantity <= 1}
                className="p-2 hover:bg-muted transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="Minus" size={16} />
              </button>
              <span className="px-4 py-2 font-mono text-center min-w-[60px]">
                {quantity}
              </span>
              <button
                onClick={() => handleQuantityChange(quantity + 1)}
                className="p-2 hover:bg-muted transition-smooth"
              >
                <Icon name="Plus" size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          variant="default"
          size="lg"
          fullWidth
          className="bg-global-1 text-global-8 rounded-lg px-4 sm:px-6 lg:px-7 py-2 sm:py-2.5 lg:py-3"
          onClick={handleAddToCart}
          loading={isAddingToCart}

          // disabled={selectedVariant == null ? true : false}
        >
          {t("addToCart")}
        </Button>

        <Button
          variant="outline"
          size="lg"
          fullWidth
          onClick={handleAddToWishlist}
          iconName={isInWishlist ? "Heart" : "Heart"}
          className="rounded-lg px-4 sm:px-6 lg:px-7 py-2 sm:py-2.5 lg:py-3"
          iconPosition="left"
        >
          {isInWishlist ? t("removeFromWishlist") : t("addToWishlist")}
        </Button>
      </div>

      {/* Product Description */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-text-primary">
          {t("description")}
        </h3>
        <div className="prose prose-sm max-w-none">
          <p className="text-text-secondary leading-relaxed whitespace-pre-line">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;
